import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { QuizApiClient } from '@/infrastructure/quiz/QuizApiClient';
import { useAuthSessionStore } from '@/presentation/auth/useAuthSessionStore';
import QuizRemoteImage from '@/presentation/quiz/QuizRemoteImage';
import { useQuizAudio } from '@/presentation/quiz/useQuizAudio';
import { getOrCreateGuestAttemptId } from '@/utils/guestAttemptId';
import { preloadImageRaw } from '@/utils/imageUrl';
import { recordQuizSubmitForHistory } from '@/utils/quizSubmitDetailCache';
import QuizResultQuestionBreakdown from '@/presentation/quiz/QuizResultQuestionBreakdown';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { quizDifficultyLabel } from '@/i18n/quizDifficulty';
import { formatApiErrorMessage } from '@/utils/formatApiErrorMessage';

const fmtTime = (sec) => {
  const n = Math.max(0, Number(sec || 0));
  const m = Math.floor(n / 60);
  const s = n % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const SHUFFLE_Q_TAG = 'shuffle_questions:';
const SHUFFLE_C_TAG = 'shuffle_choices:';

function parseTags(rawTags) {
  if (Array.isArray(rawTags)) return rawTags;
  if (typeof rawTags === 'string') {
    try {
      const parsed = JSON.parse(rawTags);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function parseBoolTag(tags, prefix, fallback = false) {
  const found = tags.find((t) => typeof t === 'string' && t.startsWith(prefix));
  if (!found) return fallback;
  const raw = String(found.slice(prefix.length)).trim().toLowerCase();
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
}

function hashSeed(input) {
  let h = 2166136261 >>> 0;
  const s = String(input || '');
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed) {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let x = t;
    x = Math.imul(x ^ (x >>> 15), x | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffledIndexes(len, seed) {
  const arr = Array.from({ length: len }, (_, i) => i);
  const rnd = mulberry32(seed);
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rnd() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function PlayHomeButton() {
  const { t } = useAppTranslation();
  return (
    <Link
      to="/"
      className="inline-flex items-center gap-1.5 rounded-xl border border-[#c3c6d7] bg-white px-3 py-2 text-sm font-semibold text-[#434655] shadow-sm transition hover:border-[#004ac6] hover:text-[#004ac6]"
    >
      <span className="material-symbols-outlined !text-lg" aria-hidden>
        home
      </span>
      {t('quiz.play.home')}
    </Link>
  );
}

export default function PublicQuizPlayPage() {
  const { t } = useAppTranslation();
  const { code } = useParams();
  const session = useAuthSessionStore((s) => s.session);
  const api = React.useMemo(() => new QuizApiClient(), []);
  const [loading, setLoading] = React.useState(true);
  const [quiz, setQuiz] = React.useState(null);
  const [error, setError] = React.useState('');
  const [playerName, setPlayerName] = React.useState('');
  const [answers, setAnswers] = React.useState({});
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [result, setResult] = React.useState(null);
  const [totalTimeLeftSec, setTotalTimeLeftSec] = React.useState(null);
  const [questionTimeLeftSec, setQuestionTimeLeftSec] = React.useState(null);
  const [assetsReady, setAssetsReady] = React.useState(false);
  const [preloadProgress, setPreloadProgress] = React.useState({ done: 0, total: 0 });
  const [attemptSeed, setAttemptSeed] = React.useState(() => Date.now());

  const submitOnceRef = React.useRef(false);
  const blockAutoSubmitWhileTimerResetsRef = React.useRef(false);
  /** 'idle' | 'await_full' | 'counting' — chỉ auto-nộp câu cuối khi đã qua đủng hồ đếm thật, tránh 0 cũ từ câu trước */
  const lastQPhaseRef = React.useRef('idle');
  const timerWarnKeyRef = React.useRef(null);
  const resultSoundPlayedRef = React.useRef(false);
  const startedAtRef = React.useRef(Date.now());

  const audio = useQuizAudio();

  const quizTags = React.useMemo(() => parseTags(quiz?.tags), [quiz?.tags]);
  const shuffleQuestions = React.useMemo(
    () =>
      quiz?.shuffle_questions == null
        ? parseBoolTag(quizTags, SHUFFLE_Q_TAG, false)
        : Boolean(quiz.shuffle_questions),
    [quiz?.shuffle_questions, quizTags]
  );
  const shuffleChoices = React.useMemo(
    () =>
      quiz?.shuffle_choices == null
        ? parseBoolTag(quizTags, SHUFFLE_C_TAG, false)
        : Boolean(quiz.shuffle_choices),
    [quiz?.shuffle_choices, quizTags]
  );
  const questions = React.useMemo(() => {
    const base = quiz?.questions || [];
    if (!shuffleQuestions || base.length <= 1) return base;
    const seed = hashSeed(`${quiz?.id || ''}|${code || ''}|attempt:${attemptSeed}|question-order`);
    const idx = shuffledIndexes(base.length, seed);
    return idx.map((i) => base[i]);
  }, [quiz?.questions, quiz?.id, code, shuffleQuestions, attemptSeed]);
  // Debug (tạm tắt): mở lại khi cần kiểm tra trạng thái shuffle ngay trên UI.
  // const questionOrderDebug = React.useMemo(
  //   () => questions.map((q) => String(q?.question_order ?? '?')).join(', '),
  //   [questions]
  // );
  const total = questions.length;
  const current = total > 0 ? questions[currentIndex] : null;
  const timerMode = quiz?.timer_mode === 'per_question' ? 'per_question' : 'total';
  const questionTimeSec = Math.max(5, Number(quiz?.question_time_seconds || 30));
  const currentChoiceOrder = React.useMemo(() => {
    const choices = current?.question_type === 'choice' ? current.choices || [] : [];
    if (choices.length <= 1) return choices.map((_, i) => i);
    if (!shuffleChoices) return choices.map((_, i) => i);
    const seed = hashSeed(`${quiz?.id || ''}|${current?.id || ''}|attempt:${attemptSeed}|choice-order`);
    return shuffledIndexes(choices.length, seed);
  }, [current?.id, current?.choices, current?.question_type, quiz?.id, shuffleChoices, attemptSeed]);

  React.useEffect(() => {
    let mounted = true;
    if (!code) return undefined;
    setLoading(true);
    setAssetsReady(false);
    api
      .getPublicQuizByCode(code)
      .then((data) => {
        if (!mounted) return;
        setQuiz(data);
      })
      .catch((e) => setError(formatApiErrorMessage(e?.message, t) || t('errors.loadQuizPlay')))
      .finally(() => setLoading(false));
    return () => {
      mounted = false;
    };
  }, [api, code, t]);

  React.useEffect(() => {
    if (!quiz?.id) return;
    setAttemptSeed(Date.now());
  }, [quiz?.id]);

  React.useEffect(() => {
    if (!quiz || result) return undefined;
    const imgs = (quiz.questions || []).map((q) => q.image_url).filter(Boolean);
    if (imgs.length === 0) {
      setPreloadProgress({ done: 0, total: 0 });
      setAssetsReady(true);
      return undefined;
    }
    setAssetsReady(false);
    setPreloadProgress({ done: 0, total: imgs.length });
    let cancelled = false;
    let done = 0;
    const bump = () => {
      if (cancelled) return;
      done += 1;
      setPreloadProgress({ done, total: imgs.length });
      if (done >= imgs.length) setAssetsReady(true);
    };
    imgs.forEach((raw) => {
      preloadImageRaw(raw).then(bump);
    });
    return () => {
      cancelled = true;
    };
  }, [quiz?.id, result]);

  React.useEffect(() => {
    if (!result && assetsReady) startedAtRef.current = Date.now();
  }, [assetsReady, result, quiz?.id]);

  React.useEffect(() => {
    if (assetsReady && timerMode === 'per_question') {
      lastQPhaseRef.current = 'idle';
    }
  }, [assetsReady, timerMode, quiz?.id]);

  const submit = React.useCallback(
    async (event) => {
      event?.preventDefault();
      if (!code || submitOnceRef.current) return;
      submitOnceRef.current = true;
      audio.playSubmitClick();
      try {
        const now = Date.now();
        const elapsedSec = Math.max(0, Math.round((now - startedAtRef.current) / 1000));
        const actorId =
          session?.accessToken && session?.user?.id
            ? String(session.user.id)
            : getOrCreateGuestAttemptId();
        const data = await api.submitPublicQuizByCode(code, {
          player_name: playerName || 'Khách',
          attempt_actor_id: actorId,
          answers,
          time_taken: elapsedSec,
        }, session?.accessToken || null);
        setResult(data);
        recordQuizSubmitForHistory({
          quizResultId: data?.quiz_result_id,
          quizId: quiz?.id,
          score: data?.score,
          correct_answers: data?.correct_answers,
          total_questions: data?.total_questions,
          passed: data?.passed,
          pass_score: data?.pass_score,
          question_results: data?.question_results,
        });
      } catch (e) {
        audio.playSubmitError();
        setError(formatApiErrorMessage(e?.message, t) || t('errors.submitQuiz'));
        submitOnceRef.current = false;
      }
    },
    [api, code, playerName, answers, audio, session?.accessToken, session?.user?.id, quiz?.id, t]
  );

  React.useEffect(() => {
    if (!quiz || result || !assetsReady) return undefined;
    submitOnceRef.current = false;
    if (timerMode === 'total') {
      const min = Number(quiz.time_limit_minutes || 0);
      if (min <= 0) {
        setTotalTimeLeftSec(null);
        return undefined;
      }
      setTotalTimeLeftSec(min * 60);
      const t = setInterval(() => {
        setTotalTimeLeftSec((prev) => {
          if (prev == null || prev <= 1) return 0;
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(t);
    }
    setQuestionTimeLeftSec(questionTimeSec);
    const t = setInterval(() => {
      setQuestionTimeLeftSec((prev) => {
        if (prev == null || prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [quiz?.id, quiz?.time_limit_minutes, timerMode, questionTimeSec, result, assetsReady]);

  React.useEffect(() => {
    if (timerMode !== 'per_question' || result || !quiz || !assetsReady) return;
    blockAutoSubmitWhileTimerResetsRef.current = true;
    setQuestionTimeLeftSec(questionTimeSec);
  }, [currentIndex, timerMode, questionTimeSec, result, quiz?.id, assetsReady]);

  React.useEffect(() => {
    if (timerMode !== 'per_question' || result) return;
    if (questionTimeLeftSec === questionTimeSec) {
      blockAutoSubmitWhileTimerResetsRef.current = false;
    }
  }, [questionTimeLeftSec, questionTimeSec, timerMode, result]);

  React.useEffect(() => {
    if (timerMode !== 'per_question' || result) {
      lastQPhaseRef.current = 'idle';
      return;
    }
    if (currentIndex < total - 1) {
      lastQPhaseRef.current = 'idle';
      return;
    }
    if (questionTimeLeftSec === questionTimeSec) {
      lastQPhaseRef.current = 'await_full';
    } else if (
      lastQPhaseRef.current === 'await_full' &&
      questionTimeLeftSec != null &&
      questionTimeLeftSec < questionTimeSec
    ) {
      lastQPhaseRef.current = 'counting';
    }
  }, [timerMode, result, currentIndex, total, questionTimeLeftSec, questionTimeSec]);

  React.useEffect(() => {
    if (result || total <= 0 || !assetsReady) return;
    if (timerMode === 'total' && totalTimeLeftSec === 0) {
      submit();
      return;
    }
    if (timerMode === 'per_question' && questionTimeLeftSec === 0) {
      if (blockAutoSubmitWhileTimerResetsRef.current) return;
      if (currentIndex >= total - 1) {
        if (lastQPhaseRef.current !== 'counting') return;
        submit();
      } else {
        setCurrentIndex((i) => Math.min(total - 1, i + 1));
      }
    }
  }, [timerMode, totalTimeLeftSec, questionTimeLeftSec, currentIndex, total, result, submit, assetsReady]);

  React.useEffect(() => {
    if (result || !quiz || !assetsReady) return;
    const sec = timerMode === 'total' ? totalTimeLeftSec : questionTimeLeftSec;
    if (sec !== 5) return;
    const key = timerMode === 'total' ? 'total-5' : `q-${currentIndex}-5`;
    if (timerWarnKeyRef.current === key) return;
    timerWarnKeyRef.current = key;
    audio.playTimerUrgent();
  }, [
    timerMode,
    totalTimeLeftSec,
    questionTimeLeftSec,
    currentIndex,
    result,
    quiz,
    assetsReady,
    audio,
  ]);

  React.useEffect(() => {
    if (!result) {
      resultSoundPlayedRef.current = false;
      return;
    }
    if (resultSoundPlayedRef.current) return;
    resultSoundPlayedRef.current = true;
    if (result.passed) audio.playResultPass();
    else audio.playResultFail();
  }, [result, audio]);

  if (loading) {
    return (
      <main className="relative mx-auto flex min-h-[40vh] max-w-4xl flex-col items-center justify-center gap-4 p-8">
        <div className="absolute right-4 top-4 md:right-8 md:top-8">
          <PlayHomeButton />
        </div>
        <span className="material-symbols-outlined animate-pulse text-5xl text-[#004ac6]">hourglass_top</span>
        <p className="font-headline text-lg font-bold text-[#191b23]">{t('quiz.play.loading')}</p>
      </main>
    );
  }
  if (error && !quiz) {
    return (
      <main className="relative mx-auto max-w-4xl p-8">
        <div className="mb-4 flex justify-end">
          <PlayHomeButton />
        </div>
        <p className="text-rose-700">{error}</p>
      </main>
    );
  }
  if (!quiz) return null;

  if (!result && !assetsReady) {
    const { done, total: preTotal } = preloadProgress;
    const pct = preTotal > 0 ? Math.round((done / preTotal) * 100) : 0;
    return (
      <main className="mx-auto max-w-lg space-y-6 p-8">
        <div className="flex justify-end">
          <PlayHomeButton />
        </div>
        <div className="rounded-2xl border border-[#c3c6d7]/40 bg-white p-8 text-center shadow-sm">
          <div className="relative mx-auto flex h-20 w-20 items-center justify-center">
            <span
              className="absolute h-20 w-20 rounded-full border-4 border-[#dbe1ff] border-t-[#004ac6] animate-spin"
              aria-hidden
            />
            <span className="material-symbols-outlined relative text-4xl text-[#004ac6] animate-pulse">quiz</span>
          </div>
          <h2 className="mt-5 font-headline text-xl font-extrabold text-[#191b23]">{t('quiz.play.preparingTitle')}</h2>
          <p className="mt-2 text-sm text-[#434655]">{t('quiz.play.preparingHint')}</p>
          <div className="mt-6 h-3 w-full overflow-hidden rounded-full bg-[#e1e2ed]">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#004ac6] to-[#2563eb] transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="mt-2 font-mono text-lg font-bold text-[#003ea8]">{pct}%</p>
          <div className="mt-5 flex justify-center gap-1.5" aria-hidden>
            <span className="h-2 w-2 rounded-full bg-[#004ac6] animate-bounce [animation-delay:0ms]" />
            <span className="h-2 w-2 rounded-full bg-[#2563eb] animate-bounce [animation-delay:150ms]" />
            <span className="h-2 w-2 rounded-full bg-[#004ac6] animate-bounce [animation-delay:300ms]" />
          </div>
        </div>
      </main>
    );
  }

  const answeredCount = questions.reduce((acc, q) => acc + (String(answers[q.id] || '').trim() ? 1 : 0), 0);

  return (
    <main className="mx-auto max-w-6xl space-y-4 bg-[#faf8ff] p-3 md:space-y-6 md:p-6">
      <div className="flex justify-end">
        <PlayHomeButton />
      </div>
      <section className="rounded-2xl border border-[#c3c6d7]/40 bg-white p-4 shadow-sm md:p-6">
        <h1 className="font-headline text-2xl font-extrabold text-slate-900 md:text-3xl">{quiz.title}</h1>
        <p className="mt-2 text-sm text-slate-600 md:text-base">{quiz.description || t('quiz.play.defaultDesc')}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs font-semibold text-[#434655]">
          <span>
            {t('quiz.play.metaCode')}: {quiz.share_code}
          </span>
          <span>|</span>
          <span>
            {t('quiz.play.metaPass')}: {quiz.pass_score}%
          </span>
          <span>|</span>
          <span>
            {t('quiz.play.metaMode')}: {timerMode === 'per_question' ? t('quiz.play.timerPerQuestion') : t('quiz.play.timerTotal')}
          </span>
          <span>|</span>
          <span>{quizDifficultyLabel(t, quiz.difficulty)}</span>
          {timerMode === 'total' && quiz.time_limit_minutes ? (
            <>
              <span>|</span>
              <span className="rounded-xl bg-[#dbe1ff] px-3 py-1 font-mono text-lg font-extrabold text-[#003ea8] md:text-2xl">
                {fmtTime(totalTimeLeftSec)}
              </span>
            </>
          ) : null}
          {timerMode === 'per_question' ? (
            <>
              <span>|</span>
              <span className="rounded-xl bg-[#ffdad6] px-3 py-1 font-mono text-lg font-extrabold text-[#93000a] md:text-2xl">
                {fmtTime(questionTimeLeftSec)}
              </span>
            </>
          ) : null}
        </div>
        {/* Debug shuffle (tạm tắt):
        <p className="mt-2 rounded-lg bg-slate-100 px-3 py-2 text-[11px] font-mono text-slate-600">
          Debug shuffle — questions: {shuffleQuestions ? 'ON' : 'OFF'} | choices: {shuffleChoices ? 'ON' : 'OFF'} |
          attemptSeed: {attemptSeed} | order(question_order): [{questionOrderDebug || 'n/a'}]
        </p>
        */}
      </section>

      {error ? <p className="text-sm text-rose-600">{error}</p> : null}

      {result ? (
        <section className="space-y-5">
          <div className="rounded-2xl border border-[#c3c6d7]/40 bg-white p-6">
            <h2 className="font-headline text-3xl font-extrabold text-[#191b23] md:text-4xl">{t('quiz.play.resultTitle')}</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl bg-[#dbe1ff] p-4">
                <p className="text-xs font-bold uppercase text-[#003ea8]">{t('quiz.play.score')}</p>
                <p className="mt-1 text-3xl font-extrabold text-[#004ac6] md:text-4xl">{result.score}%</p>
              </div>
              <div className="rounded-xl bg-[#6cf8bb]/30 p-4">
                <p className="text-xs font-bold uppercase text-[#005236]">{t('quiz.play.correct')}</p>
                <p className="mt-1 text-3xl font-extrabold text-[#006c49] md:text-4xl">
                  {result.correct_answers}/{result.total_questions}
                </p>
              </div>
              <div className="rounded-xl bg-[#ffecea] p-4">
                <p className="text-xs font-bold uppercase text-[#930013]">{t('quiz.play.status')}</p>
                <p className="mt-1 text-2xl font-extrabold text-[#ab0b1c] md:text-3xl">
                  {result.passed ? t('quiz.play.passed') : t('quiz.play.notPassed')}
                </p>
              </div>
            </div>
            <p className="mt-3 text-base font-semibold text-[#434655]">{t('quiz.play.passMark', { pct: result.pass_score })}</p>
            <button
              type="button"
              onClick={() => {
                setResult(null);
                setCurrentIndex(0);
                setAnswers({});
                setAttemptSeed(Date.now());
                startedAtRef.current = Date.now();
                setError('');
                setAssetsReady(false);
                submitOnceRef.current = false;
                blockAutoSubmitWhileTimerResetsRef.current = true;
                lastQPhaseRef.current = 'idle';
                timerWarnKeyRef.current = null;
                resultSoundPlayedRef.current = false;
              }}
              className="mt-4 rounded-xl bg-gradient-to-r from-[#004ac6] to-[#2563eb] px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-[#004ac6]/20"
            >
              {t('quiz.play.retry')}
            </button>
          </div>
          <QuizResultQuestionBreakdown questionResults={result?.question_results} />
        </section>
      ) : (
        <form onSubmit={(e) => e.preventDefault()} className="grid gap-4 lg:grid-cols-12">
          <div className="rounded-2xl border border-[#c3c6d7]/40 bg-white p-4 shadow-sm lg:col-span-12">
            <label className="block text-sm font-semibold">{t('quiz.play.playerLabel')}</label>
            <input
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              placeholder={t('quiz.play.playerPh')}
            />
          </div>

          <aside className="rounded-2xl border border-[#c3c6d7]/40 bg-white p-4 shadow-sm lg:col-span-4">
            <p className="text-sm font-bold text-[#191b23]">{t('quiz.play.minimap')}</p>
            <div className="mt-3 max-h-[13.5rem] overflow-y-auto rounded-xl border border-slate-100 p-2">
              <div className="grid grid-cols-5 gap-2">
                {questions.map((q, idx) => {
                  const active = idx === currentIndex;
                  const answered = Boolean(String(answers[q.id] || '').trim());
                  return (
                    <button
                      key={q.id}
                      type="button"
                      onClick={() => {
                        audio.playNav();
                        setCurrentIndex(idx);
                      }}
                      className={`h-9 rounded-lg text-xs font-bold ${
                        active
                          ? 'bg-[#004ac6] text-white'
                          : answered
                            ? 'bg-[#6cf8bb]/40 text-[#005236]'
                            : 'bg-[#f3f3fe] text-[#434655] hover:bg-[#dbe1ff]'
                      }`}
                    >
                      {idx + 1}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className="rounded-full bg-[#dbe1ff] px-2 py-1 text-[#003ea8]">{t('quiz.play.legendCurrent')}</span>
              <span className="rounded-full bg-[#6cf8bb]/40 px-2 py-1 text-[#005236]">{t('quiz.play.legendAnswered')}</span>
              <span className="rounded-full bg-[#f3f3fe] px-2 py-1 text-[#434655]">{t('quiz.play.legendTodo')}</span>
            </div>
          </aside>

          {current ? (
            <article className="rounded-2xl border border-[#c3c6d7]/40 bg-gradient-to-br from-[#f3f3fe] via-white to-[#dbe1ff]/30 p-5 shadow-sm md:p-6 lg:col-span-8">
              <div className="mb-4 space-y-3">
                <p className="text-xl font-extrabold text-[#003ea8] md:text-2xl">
                  {t('quiz.play.answeredOf', { done: answeredCount, total })}
                </p>
                <div className="h-3 w-full rounded-full bg-[#e1e2ed]">
                  <div
                    className="h-3 rounded-full bg-gradient-to-r from-[#004ac6] to-[#2563eb] transition-all"
                    style={{ width: `${(answeredCount / Math.max(total, 1)) * 100}%` }}
                  />
                </div>
              </div>

              <div className="rounded-xl border border-[#b4c5ff]/70 bg-[#dbe1ff]/50 p-4 md:p-5">
                <p className="text-xs font-bold uppercase tracking-wide text-[#004ac6] md:text-sm">
                  {t('quiz.play.questionN', { n: currentIndex + 1 })}
                </p>
                <p className="mt-1 text-[0.95rem] font-extrabold leading-snug text-[#191b23] md:text-xl lg:text-2xl">
                  {current.question_text}
                </p>
              </div>

              {current.hint ? (
                <p className="mt-3 rounded-xl bg-[#f3f3fe] px-3 py-2.5 text-sm leading-relaxed text-[#434655] md:text-base">
                  <span className="font-semibold text-[#003ea8]">{t('quiz.play.hint')} </span>
                  {current.hint}
                </p>
              ) : null}

              {current.image_url ? (
                <div
                  key={current.id}
                  className="mb-4 mt-3 overflow-hidden rounded-xl border border-slate-100 bg-white"
                >
                  <QuizRemoteImage
                    rawUrl={current.image_url}
                    questionId={current.id}
                    className="max-h-72 w-full object-contain"
                  />
                </div>
              ) : null}

              {current.question_type === 'choice' ? (
                <div className="mt-4 space-y-2">
                  {(current.choices || []).map((_, cIdx) => {
                    const originalIdx = currentChoiceOrder[cIdx] ?? cIdx;
                    const value = String.fromCharCode(65 + cIdx);
                    const originalLetter = String.fromCharCode(65 + originalIdx);
                    const selected = answers[current.id] === originalLetter;
                    const choiceText = current.choices?.[originalIdx] || '';
                    return (
                      <label
                        key={`${current.id}-${value}`}
                        className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
                          selected
                            ? 'border-[#004ac6] bg-[#dbe1ff]'
                            : 'border-[#e1e2ed] bg-white hover:border-[#b4c5ff] hover:bg-[#f3f3fe]'
                        }`}
                      >
                        <input
                          type="radio"
                          name={current.id}
                          value={value}
                          checked={selected}
                          onChange={() => {
                            audio.playSelect();
                            setAnswers((prev) => ({ ...prev, [current.id]: originalLetter }));
                          }}
                          className="mt-1"
                        />
                        <span className="text-sm leading-relaxed text-slate-800">
                          <strong className="text-[#003ea8]">{value}.</strong> {choiceText}
                        </span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <input
                  value={answers[current.id] || ''}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [current.id]: e.target.value }))}
                  className="mt-4 w-full rounded-xl border border-[#c3c6d7] bg-white px-3 py-2 text-sm"
                  placeholder={t('quiz.play.answerPh')}
                />
              )}
            </article>
          ) : null}

          <div className="flex flex-wrap gap-3 lg:col-span-8 lg:col-start-5">
            <button
              type="button"
              disabled={currentIndex <= 0}
              onClick={() => {
                audio.playNav();
                setCurrentIndex((i) => Math.max(0, i - 1));
              }}
              className="rounded-xl border border-[#c3c6d7] px-4 py-2 text-sm font-semibold text-[#434655] disabled:opacity-40"
            >
              {t('quiz.play.prev')}
            </button>
            {currentIndex < total - 1 ? (
              <button
                type="button"
                onClick={() => {
                  audio.playNav();
                  setCurrentIndex((i) => Math.min(total - 1, i + 1));
                }}
                className="rounded-xl bg-gradient-to-r from-[#004ac6] to-[#2563eb] px-4 py-2 text-sm font-bold text-white"
              >
                {t('quiz.play.next')}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => submit()}
                className="rounded-xl bg-gradient-to-r from-[#006c49] to-[#00714d] px-4 py-2 text-sm font-bold text-white"
              >
                {t('quiz.play.submit')}
              </button>
            )}
          </div>
        </form>
      )}
    </main>
  );
}
